import { watch } from "vue";
import { useRoute } from "vue-router";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { toast } from "vue-sonner";
import { useCurrentUser } from "vuefire";
import { functions, db } from "@/firebase";
import { useUserProfile } from "@/userProfile";
import type { TeamInvite } from "@/model";
import { workspaceDisplayName } from "@/useWorkspace";
import { markJoinedWorkspaceViaInvite } from "@/state/combinedInviteMarker";

// Module-level set: once we've shown a toast for an invite in this session,
// don't show it again until the page reloads (end of session). The user's
// request: "show till resolved" — so acceptance/decline (which delete the
// invite) naturally stops the toast on the *next* session, and within the
// current session we only surface each invite once.
const shownInviteIds = new Set<string>();

const acceptTeamInviteFn = httpsCallable<
  { teamId: string; inviteId: string },
  { success: true }
>(functions, "acceptTeamInvite");

const declineTeamInviteFn = httpsCallable<
  { teamId: string; inviteId: string },
  { success: true }
>(functions, "declineTeamInvite");

async function fetchTeamName(teamId: string): Promise<string> {
  try {
    const snap = await getDoc(doc(db, `teams/${teamId}`));
    if (snap.exists()) {
      return (snap.data()?.name as string) ?? "a team";
    }
  } catch {
    // Fall through to default.
  }
  return "a team";
}

/** Display name for a workspace, falling back to the prettified id. */
async function fetchWorkspaceName(workspaceId: string): Promise<string> {
  try {
    const snap = await getDoc(doc(db, `workspaces/${workspaceId}`));
    if (snap.exists()) {
      return workspaceDisplayName(workspaceId, snap.data()?.name as string);
    }
  } catch {
    // Fall through to default.
  }
  return workspaceDisplayName(workspaceId);
}

async function showInviteToast(invite: TeamInvite) {
  const teamName = await fetchTeamName(invite.teamId);

  // "Combined" invites also grant a workspace seat on accept — name both so the
  // invitee knows exactly what they're joining.
  const title = invite.workspaceId
    ? `You have been invited to join ${teamName} Team & ${await fetchWorkspaceName(
        invite.workspaceId
      )} Workspace`
    : `You've been invited to join ${teamName}`;

  const toastId = toast(title, {
    duration: Infinity,
    action: {
      label: "Accept",
      onClick: async () => {
        try {
          // Stash the granted workspace BEFORE the call so userst's realtime
          // user-doc listener can enrich the "You've joined" toast it fires the
          // moment the team field updates. No reload needed — team context
          // rehydrates reactively.
          if (invite.workspaceId) {
            markJoinedWorkspaceViaInvite(invite.workspaceId);
          }
          await acceptTeamInviteFn({
            teamId: invite.teamId,
            inviteId: invite.id,
          });
          toast.dismiss(toastId);
        } catch (err: any) {
          toast.error(err?.message || "Failed to accept invite");
        }
      },
    },
    cancel: {
      label: "Decline",
      onClick: async () => {
        try {
          await declineTeamInviteFn({
            teamId: invite.teamId,
            inviteId: invite.id,
          });
          toast.dismiss(toastId);
          toast("Invitation declined");
        } catch (err: any) {
          toast.error(err?.message || "Failed to decline invite");
        }
      },
    },
  });
}

/**
 * Surface pending team invites as toasts for the signed-in user.
 *
 * Runs as a side-effect in `App.vue`. For each unseen pending team invite,
 * shows a persistent sonner toast with Accept / Decline action buttons — but
 * only once the authenticated app shell is up (a `requiresAuth` route), never
 * on the login / signup / invite-accept screens. Within a single page-load
 * session we only show a given invite's toast once (tracked in
 * `shownInviteIds`), so navigating around the app doesn't spam.
 *
 * Invites remain in Firestore until accepted or declined, so on the *next*
 * session a still-pending invite will show its toast again — matching the
 * "show till resolved" product decision.
 */
export function useTeamInviteToasts() {
  const { userTeamInvites } = useUserProfile();
  const user = useCurrentUser();
  const route = useRoute();

  watch(
    [
      userTeamInvites,
      // Re-evaluate when we navigate onto/off an authenticated app route or
      // when the user's verified state flips, so the gate below stays correct
      // across SPA login navigations (login pushes /login → workspace without
      // a reload, so `userTeamInvites` alone wouldn't re-fire the watch).
      () => route.meta.requiresAuth === true,
      () => user.value?.emailVerified === true,
    ],
    ([invites, onAppRoute, emailVerified]) => {
      if (!invites) return;
      // Only surface invites once the authenticated Briefly app shell is up —
      // never on the login / signup / welcome / invite-accept screens, which
      // are the non-`requiresAuth` routes a signed-in-but-transitioning user
      // passes through. This is what keeps the toast from flashing mid-signup:
      // the team-invite signup flow creates an already-`emailVerified` user
      // (so the emailVerified guard alone wouldn't suppress it), but it does so
      // on the `/signup` route, which is not `requiresAuth`.
      if (!onAppRoute) return;
      // Defense in depth for the plain signup flow, where Firebase briefly
      // signs the user in (unverified) before signOut().
      if (!emailVerified) return;
      for (const invite of invites) {
        if (!invite?.id || !invite?.teamId) continue;
        if (shownInviteIds.has(invite.id)) continue;
        shownInviteIds.add(invite.id);
        void showInviteToast(invite);
      }
    },
    { immediate: true }
  );
}

// Exposed for tests to reset module state between runs.
export function __resetTeamInviteToastsForTest() {
  shownInviteIds.clear();
}
